import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtRM, fmtDate, downloadFile } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Download, FileSpreadsheet } from "lucide-react";

const BK_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  deposit_paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  confirmed: "bg-green-100 text-green-800 border-green-300",
  travelled: "bg-sky-50 text-sky-700 border-sky-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function Bookings() {
  const { t } = useLang();
  const [bookings, setBookings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ package_id: "", departure_id: "", customer_name: "", customer_phone: "", pax: 1, room_type: "Quad", deposit: 0 });

  const load = useCallback(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => toast.error("Failed"));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const selectedPkg = useMemo(() => packages.find((p) => p.package_id === form.package_id), [packages, form.package_id]);
  const filtered = useMemo(() => (bookings || []).filter((b) =>
    !search || b.customer_name.toLowerCase().includes(search.toLowerCase()) || b.booking_no.toLowerCase().includes(search.toLowerCase())
  ), [bookings, search]);

  const createBooking = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/bookings", {
        ...form, pax: Number(form.pax), deposit: Number(form.deposit) || 0,
      });
      toast.success(`Booking ${res.data.booking_no} created — ${fmtRM(res.data.total)}`);
      setOpen(false);
      setForm({ package_id: "", departure_id: "", customer_name: "", customer_phone: "", pax: 1, room_type: "Quad", deposit: 0 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!bookings) return <div className="flex justify-center h-64 items-center" data-testid="bookings-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="bookings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.bookings")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{bookings.length} bookings</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="booking-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search")} className="pl-9 w-52 rounded-full bg-white" />
          </div>
          <Button data-testid="export-bookings-csv" variant="outline" onClick={() => downloadFile("/reports/bookings.csv", "bookings.csv")}
            className="rounded-full border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />Export
          </Button>
          <Button data-testid="add-booking-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
            <Plus className="w-4 h-4 mr-1.5" />{t("common.add")}
          </Button>
        </div>
      </div>

      <div className="pro-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-sand-border text-left bg-[#F8F6F2]/80">
              {["Booking", "Customer", "Package", "Departure", "Pax", t("common.total"), t("common.paid"), t("common.outstanding"), t("common.status"), "INV"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.booking_id} data-testid={`booking-row-${b.booking_no}`} className="border-b border-sand-border/60 hover:bg-emerald-50/60 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-brand-800">{b.booking_no}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-brand-950">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
                </td>
                <td className="px-4 py-3 text-xs max-w-[180px] truncate">{b.package_name}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(b.departure_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{b.pax}</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtRM(b.total)}</td>
                <td className="px-4 py-3 font-mono text-xs text-emerald-700">{fmtRM(b.paid)}</td>
                <td className="px-4 py-3 font-mono text-xs text-gold-700">{fmtRM(b.outstanding)}</td>
                <td className="px-4 py-3">
                  <span data-testid={`booking-status-${b.booking_no}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${BK_STYLE[b.status] || BK_STYLE.pending}`}>
                    {b.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`download-invoice-${b.booking_no}`}
                    onClick={() => downloadFile(`/bookings/${b.booking_id}/invoice.pdf`, `INV-${b.booking_no}.pdf`)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-800 hover:bg-emerald-50 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm">No bookings found</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-booking-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">New Booking</DialogTitle></DialogHeader>
          <form onSubmit={createBooking} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Package</Label>
                <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v, departure_id: "" })} required>
                  <SelectTrigger data-testid="booking-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                  <SelectContent>
                    {packages.map((p) => <SelectItem key={p.package_id} value={p.package_id}>{p.name} — {fmtRM(p.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Departure</Label>
                <Select value={form.departure_id} onValueChange={(v) => setForm({ ...form, departure_id: v })} required disabled={!selectedPkg}>
                  <SelectTrigger data-testid="booking-departure-select"><SelectValue placeholder="Select departure" /></SelectTrigger>
                  <SelectContent>
                    {(selectedPkg?.departures || []).map((d) => {
                      const avail = d.total_seats - d.sold - d.reserved;
                      return <SelectItem key={d.departure_id} value={d.departure_id} disabled={avail <= 0}>{fmtDate(d.date)} — {avail} seats left</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer {t("common.name")}</Label>
                <Input data-testid="booking-customer-input" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>{t("common.phone")}</Label>
                <Input data-testid="booking-phone-input" required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              </div>
              <div>
                <Label>Pax</Label>
                <Input data-testid="booking-pax-input" type="number" min="1" value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} />
              </div>
              <div>
                <Label>Room Type</Label>
                <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v })}>
                  <SelectTrigger data-testid="booking-room-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Quad", "Triple", "Double", "Single"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Deposit (RM) — optional</Label>
                <Input data-testid="booking-deposit-input" type="number" min="0" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} />
              </div>
            </div>
            {selectedPkg && form.departure_id && (
              <div className="bg-sand rounded-xl p-3 text-sm flex justify-between" data-testid="booking-total-preview">
                <span className="text-muted-foreground">Estimated total</span>
                <span className="font-mono font-semibold text-brand-950">{fmtRM(selectedPkg.price * (Number(form.pax) || 1))}</span>
              </div>
            )}
            <Button data-testid="booking-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">Create Booking</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
